import React from "react";
import { AlertCircle, RefreshCw } from "lucide-react";
import { handleApiError } from "../../utils/errorHandler";

export interface WidgetErrorStateProps {
  title?: string;
  message?: string;
  error?: unknown;
  onRetry?: () => void;
  isRetrying?: boolean;
  variant?: "card" | "banner" | "compact";
  className?: string;
  "data-testid"?: string;
}

export const WidgetErrorState: React.FC<WidgetErrorStateProps> = ({
  title = "Unable to load data",
  message,
  error,
  onRetry,
  isRetrying = false,
  variant = "card",
  className = "",
  "data-testid": testId = "widget-error-state",
}) => {
  // Extract user-friendly error message, ensuring no stack traces or sensitive technical details leak
  const resolvedMessage = React.useMemo(() => {
    if (message) return message;
    if (error) {
      const parsed = handleApiError(error);
      return parsed.message;
    }
    return "An error occurred while loading this section. Please try again.";
  }, [message, error]);

  if (variant === "banner") {
    return (
      <div
        data-testid={testId}
        className={`flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 ${className}`}
      >
        <div className="flex items-center gap-2.5">
          <AlertCircle size={18} className="shrink-0 text-red-500" aria-hidden="true" />
          <span className="text-xs sm:text-sm font-medium">{resolvedMessage}</span>
        </div>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="flex items-center gap-1.5 shrink-0 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-red-700 transition-colors disabled:opacity-60 cursor-pointer"
          >
            <RefreshCw size={13} className={isRetrying ? "animate-spin" : ""} aria-hidden="true" />
            <span>{isRetrying ? "Retrying..." : "Retry"}</span>
          </button>
        )}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <div
        data-testid={testId}
        className={`flex flex-col items-center justify-center p-4 text-center ${className}`}
      >
        <AlertCircle size={22} className="text-red-500 mb-1.5" aria-hidden="true" />
        <p className="text-xs font-medium text-red-700">{resolvedMessage}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700 shadow-xs hover:bg-red-50 transition-colors disabled:opacity-60 cursor-pointer"
          >
            <RefreshCw size={12} className={isRetrying ? "animate-spin" : ""} aria-hidden="true" />
            <span>{isRetrying ? "Retrying..." : "Retry"}</span>
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      data-testid={testId}
      className={`flex flex-col items-center justify-center p-8 text-center bg-white rounded-lg border border-[#E4EAF0] shadow-xs ${className}`}
    >
      <div
        className="flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500 mb-3"
        aria-hidden="true"
      >
        <AlertCircle size={24} />
      </div>
      <h4 className="text-sm font-semibold text-[#10253A]">{title}</h4>
      <p className="mt-1 max-w-sm text-xs text-[#63788E]">{resolvedMessage}</p>

      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={isRetrying}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-primary-700 transition-colors disabled:opacity-60 cursor-pointer"
        >
          <RefreshCw size={14} className={isRetrying ? "animate-spin" : ""} aria-hidden="true" />
          <span>{isRetrying ? "Retrying..." : "Retry"}</span>
        </button>
      )}
    </div>
  );
};

export default WidgetErrorState;
